<template>
    <div class="card border-0 rounded-0 bg-white calendar-card mb-25">
        <div class="card-body p-15 p-sm-20 p-sm-25 p-lg-30 letter-spacing">
            <SpinnerLoading v-if="loading === true" />
            <div id="calendar"></div>
            <!-- ADD -->
            <div class="d-flex align-items-center justify-content-between" v-if="patient_summary?.id !== undefined">
                <span></span>
                <!-- SAVE -->
                <button :disabled="selectedTime === undefined || !dateAvailable"
                    class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16 mt-25"
                    @click="add">
                    {{ $t('cta.add_event') }}
                </button>
            </div>
        </div>
    </div>
    <EventsModal id="event_modal" :time_info="_time" :patient_summary="patient_summary"
        @save="event => saveEvent(event)" />
    <EventsModal id="update_event_modal" :event_update="_selectedEvent" :patient_summary="patient_summary"
        @update="event => updateEvent(event)" @delete="deleteConfirm" />
    <ConfirmModal id="delete_event" @confirm="deleteEvent()" :title="$t('cta.$delete.title')"
        :message="$t('cta.$delete.message')" />
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { Calendar, CalendarOptions } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayjs from "dayjs";
import enLocale from '@fullcalendar/core/locales/en-gb';
import itLocale from '@fullcalendar/core/locales/it';
import frLocale from '@fullcalendar/core/locales/fr';
import deLocale from '@fullcalendar/core/locales/de';
import esLocale from '@fullcalendar/core/locales/es';
import SpinnerLoading from '@/components/Widgets/Spinner.vue';
import EventsModal from '@/components/FK/Events/EventsModal.vue';
import API from "@/services/api/api.service";
import { EventTimeInfo, Event, calendarInputFromEvent, EventUserSummary } from "@/utils/models/event.model";
import { Modal } from "bootstrap";
import { to_bool } from "@/utils/methods/int_to_bool";
import stateStore from "@/utils/store";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";

export default defineComponent({
    name: 'EventsCalendar',
    props: {
        patient_summary: {
            type: Object as PropType<EventUserSummary>,
            required: false
        },
        add_enabled: {
            type: Boolean,
            required: false,
            default: false as boolean
        }
    },
    components: {
        SpinnerLoading,
        EventsModal,
        ConfirmModal
    },
    data() {
        return {
            loading: false,
            events: undefined,
            selectedTime: undefined,
            _time: undefined,
            _selectedEvent: undefined,
            eventModal: null as Modal | null,
            updateEventModal: null as Modal | null,
            confirmModal: null as Modal | null,
        } as {
            events?: Event[],
            loading: boolean,
            calendar?: Calendar,
            selectedTime?: EventTimeInfo,
            _time?: EventTimeInfo,
            _selectedEvent?: Event,
            eventModal: Modal | null,
            updateEventModal: Modal | null,
            confirmModal: Modal | null,
        }
    },
    mounted() {
        this.eventModal = new Modal('#event_modal', { backdrop: 'static' });
        this.updateEventModal = new Modal('#update_event_modal', { backdrop: 'static' });
        this.confirmModal = new Modal('#delete_event', { backdrop: 'static' });
        this.renderCalendar();
        // this.getEvents();
    },
    watch: {
        '$i18n.locale': function () {
            this.calendar?.setOption('locale', this.$i18n.locale);
            this.calendar?.refetchEvents();
        }
    },
    methods: {
        getEvents() {
            this.events = undefined;
            setTimeout(() => {
                this.events = [];
            }, 600);
        },
        renderCalendar() {
            const calendarEl = document.getElementById("calendar");
            if (calendarEl) {
                this.calendar = new Calendar(calendarEl, {
                    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                    headerToolbar: {
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                    },
                    locales: [enLocale, itLocale, frLocale, deLocale, esLocale],
                    locale: this.$i18n.locale,
                    initialDate: dayjs().toDate(),
                    navLinks: true,
                    editable: false,
                    dayMaxEvents: true,
                    nowIndicator: true,
                    selectable: this.patient_summary?.id !== undefined,
                    aspectRatio: 1.25,
                    defaultAllDay: false,
                    allDaySlot: false,
                    select: (args) => {
                        this.selectedTime = args as EventTimeInfo;
                        if (this.selectedTime.allDay === true) {
                            const now = dayjs();
                            this.selectedTime.start.setHours(now.add(1, 'hour').hour(), 0, 0, 0);
                            this.selectedTime.end.setDate(this.selectedTime.start.getDate());
                            this.selectedTime.end.setMonth(this.selectedTime.start.getMonth());
                            this.selectedTime.end.setFullYear(this.selectedTime.start.getFullYear());
                            this.selectedTime.end.setHours(now.add(2, 'hour').hour(), 0, 0, 0)
                        }
                        this.selectedTime.allDay = false;
                        this._time = args as EventTimeInfo;
                        console.debug('select', this.selectedTime);
                    },
                    unselect: (args) => {
                        /* setTimeout(() => {
                            this.selectedTime = undefined;
                            console.debug('unselect')
                        }, 0); */
                    },
                    eventClick: (args) => {
                        // console.debug(args.event.extendedProps);
                        //this._selectedEvent = (args.event.extendedProps as Event);
                        // this._selectedEvent = { ...args.event.extendedProps as Event, all_day: to_bool(args.event.extendedProps.all_day) };
                        this._selectedEvent = { ...args.event.extendedProps as Event, all_day: false };
                        if (this._selectedEvent.user_summary.id === stateStore.user?.info.id) {
                            this.updateEventModal?.show();
                        }
                    },
                    events: (args, successCallback, failureCallback) => {
                        console.debug(args);
                        API.getEvents({ date_from: args.start, date_to: args.end }).then((response) => {
                            if (response.data.success === true && response.data.data !== undefined) {
                                let events = (response.data.data as Event[]).map(((event: Event) => {
                                    return calendarInputFromEvent(event);
                                }));
                                console.debug(events);
                                successCallback(events);
                            }

                        }).catch((error) => {
                            failureCallback(error)
                        })
                    },

                } as CalendarOptions);
                this.calendar.render();
            }
        },
        add() {
            console.debug('add', this._time);
            this.eventModal?.show();
        },
        saveEvent(event) {
            this.calendar?.addEvent(calendarInputFromEvent(event));
            this.calendar?.refetchEvents();
            this.eventModal?.hide();
            this.selectedTime = undefined;
        },
        updateEvent(event) {
            this.calendar?.addEvent(calendarInputFromEvent(event));
            this.calendar?.refetchEvents();
            this.updateEventModal?.hide();
        },
        deleteConfirm() {
            this.updateEventModal?.hide();
            this.confirmModal?.show();
        },
        deleteEvent() {
            API.deleteEvent({ event_id: this._selectedEvent!.id }).then((response) => {
                if (response.data.success === true) {
                    this._selectedEvent = undefined;
                    this.calendar?.refetchEvents();
                }
            }).finally(() => {
                console.log("DELETED_EVENT");
            })
        }
    },
    computed: {
        dateAvailable(): boolean {
            return dayjs(this.selectedTime?.start).isSame(dayjs()) || dayjs(this.selectedTime?.start).isAfter(dayjs())
        }
    }
});


</script>